import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { renderIcon } from '../utils/renderIcon';
import {
  MissionIcon,
  DiamondIcon,
  HourglassIcon,
  ClipboardIcon,
  CampaignIcon,
} from '../../shared/ToolbarPrimitives';

export const PASTE_MISSION_TIP = 'Copy a mission first (Ctrl+C) to paste it here';

/**
 * Node type definitions for the connection menu
 * This will be expanded as more node types are added
 */
const NODE_ICONS = {
  task: MissionIcon,
  decision: DiamondIcon,
  wait: HourglassIcon,
};

export const NODE_TYPES = [
  {
    id: 'task',
    label: 'Create Mission',
    description: 'Standard mission node',
    available: true,
  },
  {
    id: 'decision',
    label: 'Create Decision',
    description: 'Yes/No branch point',
    available: true,
  },
  {
    id: 'wait',
    label: 'Create Wait for',
    description: 'Pause until something arrives',
    available: true,
  },
];

function NodeTypeButton({ nodeType, onSelect }) {
  const Icon = NODE_ICONS[nodeType.id];
  return (
    <button
      type="button"
      className={`connection-menu__item ${!nodeType.available ? 'connection-menu__item--disabled' : ''}`}
      onClick={() => nodeType.available && onSelect(nodeType.id)}
      disabled={!nodeType.available}
    >
      <span className="connection-menu__item-icon">{Icon ? <Icon /> : null}</span>
      <div className="connection-menu__item-content">
        <span className="connection-menu__item-label">{nodeType.label}</span>
        {!nodeType.available && (
          <span className="connection-menu__item-badge">Soon</span>
        )}
      </div>
    </button>
  );
}

const MENU_WIDTH = 200;
const MENU_HEIGHT = (NODE_TYPES.length + 2) * 48 + 28;

/** Screen position for the menu. The press uses this before React draws it. */
export function placeConnectionMenu(position, preferAbove) {
  let left = position.x - MENU_WIDTH / 2;
  let top = preferAbove ? position.y - MENU_HEIGHT - 8 : position.y + 8;

  if (left < 10) left = 10;
  if (left + MENU_WIDTH > window.innerWidth - 10) {
    left = window.innerWidth - MENU_WIDTH - 10;
  }
  if (!preferAbove && top + MENU_HEIGHT > window.innerHeight - 10) {
    top = position.y - MENU_HEIGHT - 8;
  }
  if (preferAbove && top < 10) {
    top = position.y + 8;
  }
  return { left, top };
}

function PasteMissionButton({ canPaste, onPaste }) {
  const button = (
    <button
      type="button"
      className={`connection-menu__item ${!canPaste ? 'connection-menu__item--disabled' : ''}`}
      onClick={() => canPaste && onPaste()}
      disabled={!canPaste}
      aria-disabled={!canPaste}
    >
      <span className="connection-menu__item-icon"><ClipboardIcon /></span>
      <div className="connection-menu__item-content">
        <span className="connection-menu__item-label">Paste Here</span>
      </div>
    </button>
  );

  if (canPaste) return button;

  return (
    <span className="connection-menu__item-tip-wrap" title={PASTE_MISSION_TIP}>
      {button}
    </span>
  );
}

/**
 * ConnectionMenu Component
 *
 * Dropdown menu that appears when clicking on a connection handle.
 * Allows user to select a node type to create and connect, or paste
 * a copied mission as a connected child.
 */
const ConnectionMenu = ({
  position, // { x, y } in screen coordinates
  onSelect, // (nodeType) => void
  onClose,
  isMultiCampaignMode = false,
  sourceCampaign = null, // The campaign this task belongs to
  checkClipboard = null,
  onCreateCampaign = null,
  preferAbove = false,
  title = 'Add Connected Node',
  open = true,
  menuRef: externalRef,
}) => {
  const menuRef = useRef(null);
  const setMenuRef = (node) => {
    menuRef.current = node;
    if (externalRef) externalRef.current = node;
  };
  const [canPaste, setCanPaste] = useState(false);

  useEffect(() => {
    let cancelled = false;
    const resolveClipboard = async () => {
      if (typeof checkClipboard === 'function') {
        try {
          const ok = await checkClipboard();
          if (!cancelled) setCanPaste(Boolean(ok));
          return;
        } catch {
          // Fall through to a direct clipboard read.
        }
      }
      try {
        const text = await navigator.clipboard.readText();
        const data = JSON.parse(text);
        if (!cancelled) {
          setCanPaste(data?.fokus === 'v2' && Array.isArray(data?.tasks) && data.tasks.length > 0);
        }
      } catch {
        if (!cancelled) setCanPaste(false);
      }
    };
    resolveClipboard();
    return () => {
      cancelled = true;
    };
  }, [checkClipboard]);

  // Close on click outside. The press that opened the menu lands on the (+)
  // itself, so that click must not dismiss it.
  useEffect(() => {
    if (!open) return undefined;
    const handleClickOutside = (e) => {
      if (e.target.closest?.('.handles-layer__connection-handle, .handles-layer__branch-btn, .task-node__handle')) return;
      if (menuRef.current && !menuRef.current.contains(e.target)) {
        onClose();
      }
    };
    
    const handleEscape = (e) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    // Delay adding listener to prevent immediate close on the same click that opened it
    const timer = setTimeout(() => {
      document.addEventListener('mousedown', handleClickOutside, true);
      document.addEventListener('click', handleClickOutside, true);
      document.addEventListener('keydown', handleEscape);
    }, 0);

    return () => {
      clearTimeout(timer);
      document.removeEventListener('mousedown', handleClickOutside, true);
      document.removeEventListener('click', handleClickOutside, true);
      document.removeEventListener('keydown', handleEscape);
    };
  }, [onClose, open]);

  // Close when canvas starts panning (trackpad or mouse drag)
  useEffect(() => {
    if (!open) return undefined;
    const handlePanStart = () => onClose();
    window.addEventListener('flowchartpanstart', handlePanStart);
    return () => window.removeEventListener('flowchartpanstart', handlePanStart);
  }, [onClose, open]);

  const placed = position ? placeConnectionMenu(position, preferAbove) : { left: 0, top: 0 };
  const { left, top } = placed;

  const showCreateCampaign = typeof onCreateCampaign === 'function';

  const handlePaste = () => {
    if (!canPaste) return;
    onSelect('paste');
  };

  const menu = (
    <div
      ref={setMenuRef}
      className={`connection-menu${open ? '' : ' connection-menu--shut'}`}
      style={{
        position: 'fixed',
        left,
        top,
        zIndex: 10000,
      }}
    >
      <div className="connection-menu__header">
        {title}
      </div>
      {isMultiCampaignMode && sourceCampaign && (
        <div className="connection-menu__campaign-context">
          <span className="connection-menu__campaign-context-icon">
            {renderIcon(sourceCampaign.icon, { fallback: '📁', size: '1em' })}
          </span>
          <span className="connection-menu__campaign-context-text">
            Adding to <strong>{sourceCampaign.name}</strong>
          </span>
        </div>
      )}
      <div className="connection-menu__list">
        {NODE_TYPES.map((nodeType) => (
          <NodeTypeButton key={nodeType.id} nodeType={nodeType} onSelect={onSelect} />
        ))}
        {showCreateCampaign && (
          <button
            type="button"
            className="connection-menu__item"
            onClick={() => {
              onCreateCampaign();
              onClose?.();
            }}
          >
            <span className="connection-menu__item-icon"><CampaignIcon /></span>
            <div className="connection-menu__item-content">
              <span className="connection-menu__item-label">Create Campaign</span>
            </div>
          </button>
        )}
        <div className="connection-menu__divider" />
        <PasteMissionButton canPaste={canPaste} onPaste={handlePaste} />
      </div>
    </div>
  );

  // Render via portal to escape transform context
  return ReactDOM.createPortal(menu, document.body);
};

export default ConnectionMenu;
